import { Link } from '@tanstack/react-router';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Card, CardContent, Button, Skeleton } from '@/components/ui';
import { partnerApi } from '@/lib/api-affiliate';

function fmtUsd(cents: number): string {
  return `$${(cents / 100).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return null;
  return Math.floor((Date.now() - d) / 86_400_000);
}

/** NextBestAction — single context-aware CTA. */
export default function NextBestAction() {
  const onboarding = partnerApi.onboarding.useStatus();
  const balance = partnerApi.balance.useGet();
  const minProgress = partnerApi.minPayoutProgress.useGet();
  const streak = partnerApi.streak.useGet();
  const lifetime = partnerApi.lifetime.useGet();

  const isLoading =
    onboarding.isLoading ||
    balance.isLoading ||
    minProgress.isLoading ||
    streak.isLoading ||
    lifetime.isLoading;

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton className="h-5 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2 mb-4" />
          <Skeleton className="h-9 w-40" />
        </CardContent>
      </Card>
    );
  }

  // Priority 1: onboarding incomplete → WelcomeChecklist owns this slot
  const ob = onboarding.data;
  if (
    ob &&
    !(ob.pickedOffer && ob.createdLink && ob.firstClick && ob.firstConversion)
  ) {
    return null;
  }

  const avail = balance.data?.availableCents ?? 0;
  const minPayout = minProgress.data?.minPayoutCents ?? 0;
  const totalConv = lifetime.data?.totalConversions ?? 0;
  const lastConvDays = daysSince(streak.data?.lastConversionAt ?? null);

  let title = '';
  let sub = '';
  let cta = '';
  let href = '/links?create=1';

  if (avail >= minPayout && minPayout > 0) {
    title = `${fmtUsd(avail)} доступны к выплате`;
    sub = 'Баланс выше минимального порога — можно вывести на твой кошелёк.';
    cta = 'Запросить выплату';
    href = '/payouts?create=1';
  } else if (avail > 0 && minPayout > 0 && avail < minPayout) {
    const diff = minPayout - avail;
    title = `До минимальной выплаты осталось ${fmtUsd(diff)}`;
    sub = 'Подбери оффер с хорошим EPC — конверсии быстрее закроют разрыв.';
    cta = 'Открыть каталог';
    href = '/offers';
  } else if (totalConv > 0 && (lastConvDays == null || lastConvDays >= 7)) {
    title = 'Уже неделю без конверсий';
    sub = 'Проверь, какая ссылка просела, и подкорректируй источник трафика.';
    cta = 'Открыть аналитику ссылок';
    href = '/links';
  } else {
    title = 'Добавь новый источник трафика';
    sub = 'Создай ещё одну трекинг-ссылку под новую площадку — больше касаний, больше конверсий.';
    cta = 'Создать ссылку';
    href = '/links?create=1';
  }

  return (
    <Card className="w-full">
      <CardContent className="flex items-start gap-3 sm:gap-4 min-w-0">
        <span className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-[var(--accent-soft)] text-[var(--accent-bright)]">
          <Sparkles className="h-5 w-5 shrink-0" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-[var(--text)] tracking-tight break-words">{title}</div>
          <p className="mt-1 text-[13px] text-[var(--text-muted)] break-words">{sub}</p>
          <div className="mt-3">
            <Link to={href as never}>
              <Button variant="gradient" size="sm">
                {cta}
                <ArrowRight className="h-3.5 w-3.5 shrink-0" />
              </Button>
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
